import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, ArrowUpRight } from "lucide-react";

const LINKS = [
  { n: "01", label: "Manifesto", href: "#manifesto" },
  { n: "02", label: "Estimator", href: "#estimator" },
  { n: "03", label: "Transformation", href: "#transformation" },
  { n: "04", label: "Contact", href: "#contact" },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <motion.header
      initial={{ y: -80, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1], delay: 0.2 }}
      data-testid="site-navbar"
      className={`fixed inset-x-0 top-0 z-50 transition-[background-color,border-color,backdrop-filter] duration-500 ${
        scrolled ? "border-b border-white/10 bg-[#090A0E]/85 backdrop-blur-xl" : "border-b border-transparent"
      }`}
    >
      <div className="mx-auto flex h-[72px] max-w-7xl items-center justify-between px-4 sm:px-6 lg:px-8">
        <a href="#top" data-testid="nav-brand-link" className="group flex items-center">
          <img
            src="/media/logo.png"
            alt="Elite Mobile Detail"
            data-testid="nav-brand-logo"
            className="h-11 w-auto transition-[opacity,transform] duration-300 group-hover:scale-[1.03] group-hover:opacity-90 sm:h-12"
          />
        </a>

        <nav className="hidden items-center gap-8 lg:flex" data-testid="nav-links-desktop">
          {LINKS.map((l) => (
            <a
              key={l.n}
              href={l.href}
              data-testid={`nav-link-${l.label.toLowerCase()}`}
              className="group font-mono text-xs uppercase tracking-[0.2em] text-slate-400 transition-colors hover:text-white"
            >
              <span className="mr-1.5 text-[#FFB703]/70">{l.n}</span>
              <span className="relative">
                {l.label}
                <span className="absolute -bottom-1 left-0 h-px w-0 bg-[#FFB703] transition-[width] duration-300 group-hover:w-full" />
              </span>
            </a>
          ))}
        </nav>

        <div className="flex items-center gap-3">
          <a
            href="#estimator"
            data-testid="nav-cta-estimator"
            className="group hidden items-center gap-2 rounded-full bg-[#FFB703] px-5 py-2.5 font-mono text-xs font-semibold uppercase tracking-[0.15em] text-[#090A0E] transition-[background-color,box-shadow] duration-300 hover:bg-[#FFA200] hover:shadow-[0_0_28px_rgba(255,183,3,0.35)] sm:flex"
          >
            Instant Estimator
            <ArrowUpRight className="h-3.5 w-3.5 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
          </a>
          <button
            type="button"
            data-testid="nav-menu-toggle"
            aria-label="Toggle menu"
            onClick={() => setOpen((v) => !v)}
            className="rounded-full border border-white/10 p-2.5 text-slate-300 transition-colors hover:border-[#FFB703]/50 hover:text-white lg:hidden"
          >
            {open ? <X className="h-4 w-4" /> : <Menu className="h-4 w-4" />}
          </button>
        </div>
      </div>

      <AnimatePresence>
        {open && (
          <motion.nav
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
            data-testid="nav-mobile-drawer"
            className="overflow-hidden border-b border-white/10 bg-[#0C0E14]/95 backdrop-blur-xl lg:hidden"
          >
            <div className="flex flex-col gap-1 px-6 py-6">
              {LINKS.map((l) => (
                <a
                  key={l.n}
                  href={l.href}
                  data-testid={`nav-mobile-link-${l.label.toLowerCase()}`}
                  onClick={() => setOpen(false)}
                  className="flex items-baseline gap-3 border-b border-white/5 py-3 font-mono text-sm uppercase tracking-[0.2em] text-slate-300 transition-colors hover:text-[#FFB703]"
                >
                  <span className="text-xs text-[#FFB703]/70">{l.n}</span> {l.label}
                </a>
              ))}
              <a
                href="#estimator"
                data-testid="nav-mobile-cta-estimator"
                onClick={() => setOpen(false)}
                className="mt-4 flex items-center justify-center gap-2 rounded-full bg-[#FFB703] px-5 py-3.5 font-mono text-xs font-semibold uppercase tracking-[0.15em] text-[#090A0E] transition-colors duration-300 hover:bg-[#FFA200]"
              >
                Instant Estimator
                <ArrowUpRight className="h-3.5 w-3.5" />
              </a>
            </div>
          </motion.nav>
        )}
      </AnimatePresence>
    </motion.header>
  );
}
